import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import useSEO from '../HireWithUs/Pages/HomePage/useSEO';
import Header from '../HireWithUs/Pages/Header&Footer/Header';
import Footer from '../HireWithUs/Pages/Header&Footer/Footer';
import { Sparkles, ArrowRight } from 'lucide-react';
import { getApiUrl } from '../config/api';

const fmtBlogDate = (iso) => {
  try { return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }); }
  catch { return ''; }
};

/**
 * Blog / Resources listing — restyled to match the SearchJob.ai
 * landing theme (cream + gradient-mesh hero, eyebrow pill, gradient
 * heading accent, rounded-3xl post cards with hover glow). Post data
 * and the /blog/:id routing are preserved from the original.
 */
const BlogHome = () => {
  useSEO({
    title: 'Resources & Insights — SearchJob.ai Blog on AI Recruitment',
    description:
      'Tips, trends and strategies on AI-powered recruitment — from sourcing and screening to AI interviews and offers.',
    canonical: 'https://searchjob.ai/blog',
    ogImage: 'https://searchjob.ai/og-image.png',
  });

  // Super-admin-authored posts from the API (newest first), shown ABOVE the
  // three original hardcoded posts below.
  const [dbPosts, setDbPosts] = useState([]);
  useEffect(() => {
    let alive = true;
    fetch(getApiUrl('/api/blogs'))
      .then((r) => (r.ok ? r.json() : []))
      .then((rows) => {
        if (!alive || !Array.isArray(rows)) return;
        setDbPosts(rows.map((b) => ({
          id: b.id,
          title: b.title,
          excerpt: b.excerpt || '',
          author: b.author || 'SearchJob.ai Team',
          date: fmtBlogDate(b.created_at),
          image: b.image_url || 'https://images.unsplash.com/photo-1499750310107-5fef28a66643?w=800&h=500&fit=crop',
          category: b.category || 'Recruitment',
        })));
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const staticPosts = [
    {
      id: 1,
      title: 'The Future of Recruitment: AI + Human Intelligence',
      excerpt:
        'Discover how the combination of AI and human intelligence is revolutionizing recruitment, creating faster, fairer, and more effective hiring processes that leverage the best of both worlds.',
      author: 'SearchJob.ai Team',
      date: 'October 1, 2024',
      image:
        'https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&h=500&fit=crop&crop=center',
      category: 'Recruitment & Technology',
    },
    {
      id: 2,
      title: 'Why AI-Driven Screening Reduces Bias in Hiring (When Done Right)',
      excerpt:
        'Learn how AI-powered screening can eliminate unconscious bias in recruitment while maintaining efficiency, and discover the crucial factors for implementing ethical AI hiring systems.',
      author: 'SearchJob.ai Team',
      date: 'October 2, 2024',
      image:
        'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800&h=500&fit=crop&crop=center',
      category: 'AI & Diversity',
    },
    {
      id: 3,
      title: 'Top 5 AI Recruitment Trends to Watch in 2025',
      excerpt:
        'Explore the cutting-edge AI recruitment trends that will define talent acquisition in 2025, from predictive analytics to conversational AI interviews and bias detection systems.',
      author: 'SearchJob.ai Team',
      date: 'October 3, 2024',
      image:
        'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&h=500&fit=crop&crop=center',
      category: 'AI Trends & Recruitment',
    },
  ];

  // Admin posts first, then the original three.
  const blogPosts = [...dbPosts, ...staticPosts];

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />

      <main className="flex-1 pt-16">
        {/* ═════════════ HERO ═════════════ */}
        <section className="relative overflow-hidden bg-background isolate">
          <div className="absolute inset-0 -z-10 pointer-events-none">
            <div className="absolute top-[-15%] left-[8%] w-[55%] h-[55%] bg-primary/15 rounded-full blur-3xl" />
            <div className="absolute top-[5%] right-[5%] w-[45%] h-[45%] bg-emerald-500/10 rounded-full blur-3xl" />
          </div>
          <div
            className="absolute inset-0 -z-10 opacity-[0.12] pointer-events-none"
            style={{
              backgroundImage:
                'linear-gradient(to right, currentColor 1px, transparent 1px), linear-gradient(to bottom, currentColor 1px, transparent 1px)',
              backgroundSize: '44px 44px',
              color: 'hsl(var(--foreground) / 0.1)',
              maskImage:
                'radial-gradient(ellipse 80% 60% at 50% 0%, black 30%, transparent 80%)',
              WebkitMaskImage:
                'radial-gradient(ellipse 80% 60% at 50% 0%, black 30%, transparent 80%)',
            }}
          />
          <div className="max-w-3xl mx-auto text-center px-4 sm:px-6 lg:px-8 pt-16 lg:pt-20 pb-14">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-primary/20 bg-primary/5 text-primary text-xs font-semibold uppercase tracking-wider mb-5">
              <Sparkles className="w-3.5 h-3.5" />
              Resources
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-[-0.02em] text-foreground leading-[1.08] mb-5">
              Insights for your
              <br />
              <span className="bg-gradient-to-r from-primary via-emerald-600 to-teal-600 bg-clip-text text-transparent">
                hiring journey.
              </span>
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Tips, trends and strategies on AI-powered recruitment — from
              sourcing to offer.
            </p>
          </div>
        </section>

        {/* ═════════════ POSTS ═════════════ */}
        <section className="relative py-8 lg:py-14 bg-background">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {blogPosts.map((post) => (
                <Link
                  key={post.id}
                  to={`/blog/${post.id}`}
                  className="group relative flex flex-col bg-card border border-border/70 rounded-3xl overflow-hidden shadow-sm hover:shadow-[0_16px_50px_-16px_hsl(var(--primary)/0.28)] hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <span className="absolute top-3 left-3 text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full bg-card/90 backdrop-blur-sm border border-border text-primary">
                      {post.category}
                    </span>
                  </div>
                  <div className="flex flex-col flex-1 p-6">
                    <div className="text-[11px] text-muted-foreground mb-2">
                      {post.author} · {post.date}
                    </div>
                    <h2 className="text-lg font-bold text-foreground leading-snug mb-2 line-clamp-2 group-hover:text-primary transition-colors">
                      {post.title}
                    </h2>
                    <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3 mb-4">
                      {post.excerpt}
                    </p>
                    <span className="mt-auto inline-flex items-center gap-1 text-sm font-semibold text-primary group-hover:gap-2 transition-all">
                      Read more
                      <ArrowRight className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default BlogHome;
